import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Button, ComboboxItem, Group, Menu } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconEye, IconPencil, IconTrash } from "@tabler/icons-react";

import { openDeleteModal } from "@components/DeleteModal";
import { FilePreviewModal } from "@components/FilePreviewModal";
import { LastUpdatedTimestamp } from "@components/LastUpdated";

import {
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";

import { useDeleteRadiology } from "@hooks/mutations/radiology/useDeleteRadiology";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { useAuth } from "@contexts/auth";

import { Radiology } from "@customTypes/radiology";

import { EditModal } from "../EditModal";


interface TableControlsProps {
  radiology: Partial<Radiology>;
  radTypes: ComboboxItem[];
}

export const TableControls = ({ radiology, radTypes }: TableControlsProps) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { patientId } = useParams();

  const [
    filePreviewOpened,
    { open: filePreviewOpen, close: filePreviewClose },
  ] = useDisclosure(false);
  const [editModalOpened, { open: editModalOpen, close: editModalClose }] =
    useDisclosure(false);
  const [radiologyState, setRadiologyState] =
    useState<Partial<Radiology> | null>(null);

  const { mutate: deleteRadiology, isLoading: isDeleteRadiologyLoading } =
    useDeleteRadiology();
  const handleErrors = useQueryErrorHandling();

  useEffect(() => {
    if (radiology) {
      // console.log(row.original);
      setRadiologyState(radiology);
    }
  }, [radiology]);

  return (
    <>
      <FilePreviewModal
        opened={filePreviewOpened}
        close={filePreviewClose}
        docs={
          radiologyState?.files
            ? [
                ...radiologyState.files.map(item => ({
                  uri: "https://" + item + `?token=${token}`,
                  fileName: (item as string).split("/files/")[1],
                })),
              ]
            : []
        }
      />
      <EditModal
        opened={editModalOpened}
        close={editModalClose}
        radiology={radiology}
        radTypes={radTypes}
      />

      <Group className="table-controls" justify="end">
        <Menu withArrow position="bottom-end" withinPortal>
          <Menu.Target>
            <Button
              variant="outline"
              size="compact-sm"
              loading={isDeleteRadiologyLoading}>
              {t("action")}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              disabled={
                radiologyState?.files && radiologyState.files.length > 0
                  ? false
                  : true
              }
              leftSection={<IconEye size={16} stroke={1.5} />}
              onClick={filePreviewOpen}>
              {t("view-files")}
            </Menu.Item>

            <PermissionGate permission="radiology.edit"><Menu.Item
              leftSection={<IconPencil size={16} stroke={1.5} />}
              onClick={editModalOpen}>
              {t("edit-radiology")}
            </Menu.Item></PermissionGate>
            <PermissionGate permission="radiology.delete"><Menu.Item
              leftSection={<IconTrash size={16} stroke={1.5} />}
              color="red"
              onClick={() =>
                openDeleteModal({
                  onConfirm: () => {
                    const id = showLoadingNotification({});
                    deleteRadiology(
                      {
                        radId: radiologyState?.id as string,
                        patientId: patientId as string,
                      },
                      {
                        onSuccess: () => {
                          showSuccessNotification({
                            id,
                            title: "success",
                            message: "delete-radiology-success",
                          });
                        },
                        onError: (res: any) => {
                          handleErrors(res, "mutation", id);
                        },
                      }
                    );
                  },
                })
              }>
              {t("delete-radiology")}
            </Menu.Item></PermissionGate>
          </Menu.Dropdown>
        </Menu>
      </Group>

      <LastUpdatedTimestamp
        updatedDate={radiology?.updatedAt as Date}
        className="last-updated"
      />
    </>
  );
};
